import { Add, Edit } from '@mui/icons-material'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardContent from '@mui/material/CardContent'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import makeStyles from '@mui/styles/makeStyles'
import React, { useState } from 'react'
import { gql, useQuery } from 'urql'
import { GenericError } from '../error-pages'
import { Trash } from '../icons'
import CompList from '../lists/CompList'
import { CompListItemText } from '../lists/CompListItems'
import CreateFAB from '../lists/CreateFAB'
import Spinner from '../loading/components/Spinner'
import { useIsWidthDown } from '../util/useWidth'
import ServiceSlackListCreateDialog from './ServiceSlackListCreateDialog'
import ServiceSlackListDeleteDialog from './ServiceSlackListDeleteDialog'
import ServiceSlackListEditDialog from './ServiceSlackListEditDialog'

const query = gql`
  query ($serviceID: ID!) {
    service(id: $serviceID) {
      id
      slackLists {
        id
        slackListID
        name
        columns {
          alert
          service
          severity
          status
          assignees
        }
      }
    }
  }
`

const useStyles = makeStyles({
  spacing: {
    marginBottom: 96,
  },
})

export default function ServiceSlackLists(props: {
  serviceID: string
}): JSX.Element {
  const classes = useStyles()
  const isMobile = useIsWidthDown('md')
  const [create, setCreate] = useState<boolean>(false)
  const [editListID, setEditListID] = useState<string | null>(null)
  const [deleteDialog, setDeleteDialog] = useState<{
    id: string
    name: string
  } | null>(null)

  // NOTE: This query will currently fail until backend resolvers are implemented.
  const [{ fetching, error, data }] = useQuery({
    query,
    variables: { serviceID: props.serviceID },
  })

  if (fetching && !data) return <Spinner />
  if (error) return <GenericError error={error.message} />

  const items = (data?.service?.slackLists || []).map((list: any) => (
    <CompListItemText
      key={list.id}
      title={list.name}
      subText={`Slack List ID: ${list.slackListID}`}
      action={
        <React.Fragment>
          <IconButton
            title='Edit List'
            onClick={() => setEditListID(list.id)}
            size='large'
          >
            <Edit />
          </IconButton>
          <IconButton
            title='Delete this list'
            onClick={() => {
              setDeleteDialog({ id: list.id, name: list.name })
            }}
            size='large'
          >
            <Trash />
          </IconButton>
        </React.Fragment>
      }
    />
  ))

  return (
    <React.Fragment>
      <Grid item xs={12} className={classes.spacing}>
        <Card>
          <CardContent>
            <CompList
              note='Manage Slack Lists for this service.'
              emptyMessage='No Slack Lists exist for this service.'
              hideActionOnMobile
              action={
                <Button
                  variant='contained'
                  onClick={() => setCreate(true)}
                  startIcon={<Add />}
                >
                  Add Slack List
                </Button>
              }
            >
              {items}
            </CompList>
          </CardContent>
        </Card>
      </Grid>
      {isMobile && (
        <CreateFAB onClick={() => setCreate(true)} title='Add Slack List' />
      )}
      {create && (
        <ServiceSlackListCreateDialog
          serviceID={props.serviceID}
          onClose={() => setCreate(false)}
        />
      )}
      {editListID && (
        <ServiceSlackListEditDialog
          listID={editListID}
          onClose={() => setEditListID(null)}
        />
      )}
      {deleteDialog && (
        <ServiceSlackListDeleteDialog
          serviceID={props.serviceID}
          id={deleteDialog.id}
          name={deleteDialog.name}
          onClose={() => setDeleteDialog(null)}
        />
      )}
    </React.Fragment>
  )
}
